/**
 * Country / currency / language configuration.
 * Extracted from localization.js for modularity.
 * Imported by localization.js via re-export — consumers keep existing imports.
 */

// Private validators used inside COUNTRY_CONFIG (BR fiscal docs)
function validateCPF(cpf) {
  if (!cpf || cpf.length !== 11) return false;
  if (/^(\d)\1{10}$/.test(cpf)) return false;
  let sum = 0;
  for (let i = 0; i < 9; i++) sum += parseInt(cpf[i]) * (10 - i);
  let check = (sum * 10) % 11;
  if (check === 10 || check === 11) check = 0;
  if (check !== parseInt(cpf[9])) return false;
  sum = 0;
  for (let i = 0; i < 10; i++) sum += parseInt(cpf[i]) * (11 - i);
  check = (sum * 10) % 11;
  if (check === 10 || check === 11) check = 0;
  return check === parseInt(cpf[10]);
}

function validateCNPJ(cnpj) {
  if (!cnpj || cnpj.length !== 14) return false;
  if (/^(\d)\1{13}$/.test(cnpj)) return false;
  const w1 = [5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
  const w2 = [6, 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
  let sum = 0;
  for (let i = 0; i < 12; i++) sum += parseInt(cnpj[i]) * w1[i];
  let check = sum % 11;
  check = check < 2 ? 0 : 11 - check;
  if (check !== parseInt(cnpj[12])) return false;
  sum = 0;
  for (let i = 0; i < 13; i++) sum += parseInt(cnpj[i]) * w2[i];
  check = sum % 11;
  check = check < 2 ? 0 : 11 - check;
  return check === parseInt(cnpj[13]);
}

export const COUNTRY_CONFIG = {
  ES: {
    code: "ES", name: "España", currency: "EUR", currencySymbol: "€",
    currencyPosition: "after", locale: "es-ES", language: "es",
    fiscalDocument: {
      name: "NIF / CIF", placeholder: "B12345678", pattern: /^[A-Z0-9]{9}$/i,
      validate: (v) => /^[A-Z]\d{8}$|^\d{8}[A-Z]$|^[XYZ]\d{7}[A-Z]$/i.test(v.replace(/[^A-Z0-9]/gi, "")),
      errorMessage: "NIF/CIF/NIE inválido. Formato: B12345678, 12345678A ou Z1234567A",
    },
    phonePrefix: "+34", dateFormat: "dd/MM/yyyy", decimalSeparator: ",", thousandsSeparator: ".",
  },
  PT: {
    code: "PT", name: "Portugal", currency: "EUR", currencySymbol: "€",
    currencyPosition: "after", locale: "pt-PT", language: "pt",
    fiscalDocument: {
      name: "NIF", placeholder: "123456789", pattern: /^\d{9}$/,
      validate: (v) => {
        const nif = v.replace(/\D/g, "");
        if (nif.length !== 9) return false;
        let sum = 0;
        for (let i = 0; i < 8; i++) sum += parseInt(nif[i]) * (9 - i);
        const check = 11 - (sum % 11);
        return parseInt(nif[8]) === (check >= 10 ? 0 : check);
      },
      errorMessage: "NIF inválido. Deve ter 9 dígitos",
    },
    phonePrefix: "+351", dateFormat: "dd/MM/yyyy", decimalSeparator: ",", thousandsSeparator: ".",
  },
  BR: {
    code: "BR", name: "Brasil", currency: "BRL", currencySymbol: "R$",
    currencyPosition: "before", locale: "pt-BR", language: "pt",
    fiscalDocument: {
      name: "CPF / CNPJ", placeholder: "000.000.000-00 ou 00.000.000/0001-00",
      pattern: /^(\d{11}|\d{14})$/,
      validate: (v) => { const d = v.replace(/\D/g, ""); return d.length === 11 ? validateCPF(d) : d.length === 14 ? validateCNPJ(d) : false; },
      errorMessage: "CPF ou CNPJ inválido",
    },
    phonePrefix: "+55", dateFormat: "dd/MM/yyyy", decimalSeparator: ",", thousandsSeparator: ".",
  },
  US: {
    code: "US", name: "United States", currency: "USD", currencySymbol: "$",
    currencyPosition: "before", locale: "en-US", language: "en",
    fiscalDocument: {
      name: "EIN / SSN", placeholder: "XX-XXXXXXX", pattern: /^[\d-]+$/,
      validate: () => true, errorMessage: "Invalid EIN/SSN format",
    },
    phonePrefix: "+1", dateFormat: "MM/dd/yyyy", decimalSeparator: ".", thousandsSeparator: ",",
  },
  GB: {
    code: "GB", name: "United Kingdom", currency: "GBP", currencySymbol: "£",
    currencyPosition: "before", locale: "en-GB", language: "en",
    fiscalDocument: {
      name: "VAT Number", placeholder: "GB123456789", pattern: /^GB\d{9}$/i,
      validate: (v) => /^GB\d{9}$/i.test(v.replace(/\s/g, "")),
      errorMessage: "Invalid UK VAT number",
    },
    phonePrefix: "+44", dateFormat: "dd/MM/yyyy", decimalSeparator: ".", thousandsSeparator: ",",
  },
  FR: {
    code: "FR", name: "France", currency: "EUR", currencySymbol: "€",
    currencyPosition: "after", locale: "fr-FR", language: "fr",
    fiscalDocument: {
      name: "SIRET / SIREN", placeholder: "12345678901234",
      pattern: /^[\d\s]{9,14}$/,
      validate: (v) => { const d = v.replace(/\D/g, ""); return d.length === 9 || d.length === 14; },
      errorMessage: "SIRET (14 chiffres) ou SIREN (9 chiffres) invalide",
    },
    phonePrefix: "+33", dateFormat: "dd/MM/yyyy", decimalSeparator: ",", thousandsSeparator: " ",
  },
  DE: {
    code: "DE", name: "Deutschland", currency: "EUR", currencySymbol: "€",
    currencyPosition: "after", locale: "de-DE", language: "de",
    fiscalDocument: {
      name: "Steuernummer", placeholder: "12/345/67890",
      pattern: /^[\d\/\s]{10,13}$/,
      validate: (v) => v.replace(/\D/g, "").length >= 10,
      errorMessage: "Steuernummer ungültig (10-13 Ziffern)",
    },
    phonePrefix: "+49", dateFormat: "dd.MM.yyyy", decimalSeparator: ",", thousandsSeparator: ".",
  },
  IT: {
    code: "IT", name: "Italia", currency: "EUR", currencySymbol: "€",
    currencyPosition: "after", locale: "it-IT", language: "it",
    fiscalDocument: {
      name: "Partita IVA", placeholder: "12345678901",
      pattern: /^\d{11}$/,
      validate: (v) => /^\d{11}$/.test(v.replace(/\D/g, "")),
      errorMessage: "Partita IVA non valida (11 cifre)",
    },
    phonePrefix: "+39", dateFormat: "dd/MM/yyyy", decimalSeparator: ",", thousandsSeparator: ".",
  },
  AE: {
    code: "AE", name: "UAE", currency: "AED", currencySymbol: "AED",
    currencyPosition: "before", locale: "en-AE", language: "en",
    fiscalDocument: {
      name: "TRN", placeholder: "100123456700003",
      pattern: /^\d{15}$/,
      validate: (v) => /^\d{15}$/.test(v.replace(/\D/g, "")),
      errorMessage: "TRN must be 15 digits",
    },
    phonePrefix: "+971", dateFormat: "dd/MM/yyyy", decimalSeparator: ".", thousandsSeparator: ",",
  },
  CA: {
    code: "CA", name: "Canada", currency: "USD", currencySymbol: "$",
    currencyPosition: "before", locale: "en-CA", language: "en",
    fiscalDocument: {
      name: "BN / GST Number", placeholder: "123456789RT0001",
      pattern: /^[\w\d]{9,15}$/,
      validate: () => true,
      errorMessage: "Invalid BN/GST number",
    },
    phonePrefix: "+1", dateFormat: "MM/dd/yyyy", decimalSeparator: ".", thousandsSeparator: ",",
  },
  // Saudi Arabia — prepared, NOT activated. Requires: SAR Stripe price_ids,
  // arabic_enabled flag, rtl_enabled flag, Mada/STC Pay integration.
  SA: {
    code: "SA", name: "Saudi Arabia", currency: "SAR", currencySymbol: "﷼",
    currencyPosition: "after", locale: "ar-SA", language: "ar",
    fiscalDocument: {
      name: "CR Number", placeholder: "1010XXXXXXX", pattern: /^\d{10}$/,
      validate: (v) => /^\d{10}$/.test(v.replace(/\D/g, "")),
      errorMessage: "Invalid Saudi CR number (10 digits)",
    },
    phonePrefix: "+966", dateFormat: "dd/MM/yyyy", decimalSeparator: ".", thousandsSeparator: ",",
  },
};

export const DEFAULT_COUNTRY = "ES";

export const CURRENCIES = {
  EUR: { symbol: "€", name: "Euro", position: "after" },
  BRL: { symbol: "R$", name: "Real Brasileiro", position: "before" },
  USD: { symbol: "$", name: "US Dollar", position: "before" },
  GBP: { symbol: "£", name: "British Pound", position: "before" },
  // SAR — prepared for Saudi Arabia. NOT active in billing (no Stripe price_ids yet).
  SAR: { symbol: "﷼", name: "Riyal Saudita", position: "after" },
};

export const LANGUAGES = {
  pt: { name: "Português", nativeName: "Português" },
  es: { name: "Español", nativeName: "Español" },
  en: { name: "English", nativeName: "English" },
};

export function getCountryConfig(countryCode) {
  return COUNTRY_CONFIG[countryCode?.toUpperCase()] || COUNTRY_CONFIG[DEFAULT_COUNTRY];
}

export function getCurrencySymbol(countryCode) {
  return getCountryConfig(countryCode).currencySymbol;
}

export function getCurrencyCode(countryCode) {
  return getCountryConfig(countryCode).currency;
}

export function detectUserCountry() {
  const l = navigator.language || navigator.userLanguage;
  if (l?.startsWith("pt-BR")) return "BR";
  if (l?.startsWith("pt")) return "PT";
  if (l?.startsWith("es")) return "ES";
  if (l?.startsWith("en-US")) return "US";
  if (l?.startsWith("en-GB")) return "GB";
  if (l?.startsWith("en")) return "US";
  return DEFAULT_COUNTRY;
}

export function detectUserLanguage() {
  const l = navigator.language || navigator.userLanguage;
  if (l?.startsWith("pt")) return "pt";
  if (l?.startsWith("es")) return "es";
  if (l?.startsWith("en")) return "en";
  return "es";
}
